import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import {
  PropInstanceHorizontalAlignment,
  PropInstanceLateralAlignment,
  PropInstanceVerticalAlignment,
  PropInstanceVerticalDatumAnchor,
  WallHorizontalDatum,
} from "building-types/datums/datumsTypesEnum";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { EdgeHostedEntityPlacementNoExpr } from "building-types/entities/edgePlacementTypes";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { RoomLayoutEligibleDocEntity } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { InteriorFloorMaterialOverride } from "building-types/settings/floorMaterialSettings";
import type { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import type { PropViewNoteTable } from "building-types/settings/viewNotesSettings";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { PropMaterialOverrides } from "./propTypes";
import type { PlacementStyleVersion } from "./propTypesEnum";

export type PropInstance = PropInstanceV2 | PropInstanceV1;

/**
 * @deprecated
 */
export interface PropInstanceV1 extends PropInstanceBase {
  readonly type: "WALL";
}

export interface PropInstanceV2 extends PropInstanceBase {
  readonly type: "WALL_V2";
}

export interface PropInstanceBase
  extends Omit<EdgeHostedEntityPlacementNoExpr, "verticalOffset" | "lateralOffsetNormalized">,
    LabelableEntityBase,
    FormulaDisabledDocEntityBase<"PROP_INSTANCE">,
    RoomLayoutEligibleDocEntity {
  readonly id: PrimaryEntityId<"PROP_INSTANCE">;
  readonly parentId?: DocEntityParentId<"ROOM" | "ROOM_LAYOUT">;
  readonly edgeId: string;
  readonly catalogType: PropCatalogType;
  readonly propType: ForeignEntityId<"PROP">;
  readonly horizontalAlignment: PropInstanceHorizontalAlignment;
  readonly verticalDatum: PropInstanceVerticalDatumAnchor;
  readonly verticalAlignment: PropInstanceVerticalAlignment;
  readonly lateralAlignment: PropInstanceLateralAlignment;
  readonly rotation: number; // in degrees
  readonly mirrorPropX?: boolean;
  readonly mirrorPropY?: boolean;
  readonly floorMaterialOverride?: InteriorFloorMaterialOverride;
  readonly useExteriorPalette?: boolean;
  readonly viewNotes?: PropViewNoteTable;
  readonly materialOverrides?: PropMaterialOverrides;
  readonly floorPlanTag?: string;
  readonly floorPlanTagOffsetX?: number;
  readonly floorPlanTagOffsetY?: number;
  readonly isExterior?: boolean;
  /**
   * Inherited if left undefined
   **/
  readonly verticalOffset?: number;
  /**
   * Inherited if left undefined
   **/
  readonly lateralOffsetNormalized?: boolean;
  /*
  Determines whether the max offset of the baseline of the wall strip
  is used or the regular wall baseline. It is more intuitive to set this
  to true.
  */
  readonly useWallStripMaxOffset?: boolean;

  /*
 VERSION_0 corrects a positioning error when using centerY, centerX + mirrorPropX or centerY + mirrorPropY.
 */
  readonly placementStyle: PlacementStyleVersion;
}

export const defaultPropInstanceHorizontalDatum = WallHorizontalDatum.OUTSIDE_OF_SURFACE;
export const defaultPropInstanceHorizontalAlignment = PropInstanceHorizontalAlignment.ORIGIN;
export const defaultPropInstanceVerticalDatum = PropInstanceVerticalDatumAnchor.ROOM_FLOOR_ELEVATION;
export const defaultPropInstanceVerticalAlignment = PropInstanceVerticalAlignment.ORIGIN;
export const defaultPropInstanceLateralDatum = WallLateralDatumAnchorAll.INSIDE_FACE_OF_FRAMING;
export const defaultPropInstanceLateralAlignment = PropInstanceLateralAlignment.ORIGIN;
